import React, { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading, Spinner, Center } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };
const inp: React.CSSProperties = { display:'block',width:'100%',padding:'12px 14px',background:'#0D1118',border:`1px solid #1E2330`,borderRadius:8,color:'#F0F4FF',fontSize:14,fontFamily:"'Rajdhani','Arial',sans-serif",boxSizing:'border-box' as any };
const lbl: React.CSSProperties = { display:'block',fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#19E6CF',letterSpacing:'0.15em',marginBottom:6,textTransform:'uppercase' as const };

const EXPERIENCE_OPTIONS = ['Under 1 year','1-2 years','3-5 years','5-10 years','10+ years'];
const FREQUENCY_OPTIONS = ['Weekly','Bi-weekly','Monthly','Multiple times per week','Occasional'];
const PLAYER_COUNT_OPTIONS = ['Under 10','10-20','20-50','50-100','100+','Varies'];

const PCTDSApplicationPage: FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ td_name:'', td_email:'', td_phone:'', years_experience:'', games_per_month:'', avg_players:'', game_description:'', why_pctds:'', has_dealer:false, dealer_notes:'', references:'', agrees_to_rules:false, agrees_to_verification:false });

  useEffect(() => {
    const load = async () => {
      if (!venueId) return;
      const snap = await getDoc(doc(db, 'venues', venueId));
      if (snap.exists()) {
        const v = { id: venueId, ...snap.data() };
        setVenue(v);
        setForm(f => ({ ...f, td_name: user?.displayName ?? '' }));
        if (v.pctds_status === 'applied' || v.pctds_status === 'verified') setSubmitted(true);
      }
      setLoading(false);
    };
    load();
  }, [venueId, user]);

  const set = (field: string, value: any) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async () => {
    if (!user?.uid || !venueId) return;
    if (!form.td_email || !form.years_experience || !form.why_pctds) { setError('Please fill in all required fields.'); return; }
    if (!form.agrees_to_rules || !form.agrees_to_verification) { setError('You must agree to both checkboxes to submit.'); return; }
    setSubmitting(true); setError('');
    try {
      await addDoc(collection(db, 'pctds_applications'), {
        venue_id: venueId, venue_name: venue.name, td_uid: user.uid,
        td_name: form.td_name, td_email: form.td_email, td_phone: form.td_phone,
        years_experience: form.years_experience, games_per_month: form.games_per_month,
        avg_players: form.avg_players, game_description: form.game_description,
        why_pctds: form.why_pctds, has_dealer: form.has_dealer,
        dealer_notes: form.dealer_notes, references: form.references,
        status: 'pending', submitted_at: serverTimestamp(),
      });
      await updateDoc(doc(db, 'venues', venueId), { pctds_status: 'applied', pctds_applied_at: serverTimestamp() });
      await addDoc(collection(db, 'admin_notifications'), {
        type: 'pctds_application', venue_id: venueId, venue_name: venue.name,
        td_uid: user.uid, td_name: form.td_name, td_email: form.td_email,
        message: `New PCTDS application from ${form.td_name} for venue "${venue.name}"`,
        status: 'pending', created_at: serverTimestamp(),
      });
      setSubmitted(true);
    } catch(e) { console.error(e); setError('Something went wrong. Please try again.'); }
    setSubmitting(false);
  };

  if (loading) return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;
  if (!venue) return <Center h="400px"><Text color="gray.400">Venue not found.</Text></Center>;
  if (venue.td_uid !== user?.uid) return (
    <Center h="400px" flexDirection="column" gap={4}>
      <Text style={{ fontFamily:F.ui, fontSize:16, color:C.dim }}>Only the venue owner can apply for PCTDS verification.</Text>
      <Box as="button" px={5} py={3} bg={C.card} border={`1px solid ${C.border}`} borderRadius={8} fontSize="13px" color={C.dim} fontFamily={F.ui} cursor="pointer" onClick={() => navigate(`/venue/${venueId}`)}>Back to Venue</Box>
    </Center>
  );

  if (submitted) return (
    <Box maxW="600px" mx="auto" px={4} py={8} fontFamily={F.ui}>
      <Box bg={C.card} border={`2px solid ${C.gold}`} borderRadius={10} p={8} textAlign="center">
        <Text style={{ fontSize:52, marginBottom:16 }}>🏆</Text>
        <Heading style={{ fontFamily:F.ui, color:C.white, fontSize:26, marginBottom:8 }}>
          {venue.pctds_status === 'verified' ? 'PCTDS Verified!' : 'Application Submitted!'}
        </Heading>
        <Text style={{ fontFamily:F.ui, fontSize:14, color:C.ink, lineHeight:1.7, marginBottom:8 }}>
          Your application for <b style={{ color:C.gold }}>{venue.name}</b> is under review.
        </Text>
        <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, lineHeight:1.7, marginBottom:24 }}>
          The PokerCity team will contact you at <b style={{ color:C.ink }}>{form.td_email}</b> within 3-5 business days.
          Your venue shows a <b style={{ color:'#D97706' }}>PCTDS Pending</b> badge in the meantime.
        </Text>
        <Box as="button" px={6} py={3} bg="#0D1F1E" border={`1px solid ${C.teal}`} borderRadius={8} fontSize="13px" fontWeight="700" color={C.teal} fontFamily={F.ui} cursor="pointer" onClick={() => navigate(`/venue/${venueId}`)}>Back to Venue Profile</Box>
      </Box>
    </Box>
  );

  return (
    <Box maxW="640px" mx="auto" px={4} py={8} fontFamily={F.ui}>
      <Box as="button" mb={4} onClick={() => navigate(`/venue/${venueId}`)}>
        <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim }}>← {venue.name}</Text>
      </Box>
      <Heading style={{ fontFamily:F.ui, color:C.white, fontSize:26, marginBottom:6 }}>Apply for PCTDS Verification</Heading>
      <Text style={{ fontFamily:F.ui, fontSize:13, color:C.dim, lineHeight:1.7, marginBottom:24 }}>
        PCTDS is the official sanctioning body for PokerCity-affiliated games. Verified venues get the PCTDS badge, poster generator, sanctioned event tools, and priority placement in the game directory.
      </Text>

      <Box bg="#1A0F00" border={`1px solid ${C.gold}`} borderRadius={8} p={4} mb={6}>
        <Text style={{ fontFamily:F.mono, fontSize:11, color:C.gold, letterSpacing:'0.15em', marginBottom:10 }}>PCTDS VERIFIED BENEFITS</Text>
        {['✅ PCTDS Verified badge on your venue profile','🎨 Poster generator — branded PDF event posters with QR codes','📋 Sanctioned event tools and official results tracking','🔝 Priority placement in the Find a Game directory','📊 PCTDS leaderboard — player rankings across your events','🃏 Dealer certification requirement enforcement'].map(item => (
          <Text key={item} style={{ fontFamily:F.ui, fontSize:13, color:C.ink, lineHeight:1.8 }}>{item}</Text>
        ))}
      </Box>

      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden">
        <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
          <Text style={{ fontFamily:F.mono, fontSize:11, color:C.teal, letterSpacing:'0.2em' }}>APPLICATION FOR: {venue.name?.toUpperCase()}</Text>
        </Box>
        <Box p={5}>
          <Flex direction="column" gap={5}>
            <Box><label style={lbl}>Your Name *</label><input style={inp} value={form.td_name} onChange={e=>set('td_name',e.target.value)} /></Box>
            <Flex gap={3}>
              <Box flex={1}><label style={lbl}>Email *</label><input style={inp} type="email" placeholder="your@email.com" value={form.td_email} onChange={e=>set('td_email',e.target.value)} /></Box>
              <Box flex={1}><label style={lbl}>Phone</label><input style={inp} type="tel" placeholder="204-555-0100" value={form.td_phone} onChange={e=>set('td_phone',e.target.value)} /></Box>
            </Flex>
            <Flex gap={3}>
              <Box flex={1}><label style={lbl}>Years Running Games *</label><select style={{...inp,cursor:'pointer'}} value={form.years_experience} onChange={e=>set('years_experience',e.target.value)}><option value="">Select...</option>{EXPERIENCE_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></Box>
              <Box flex={1}><label style={lbl}>Frequency</label><select style={{...inp,cursor:'pointer'}} value={form.games_per_month} onChange={e=>set('games_per_month',e.target.value)}><option value="">Select...</option>{FREQUENCY_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></Box>
            </Flex>
            <Box><label style={lbl}>Avg Players</label><select style={{...inp,cursor:'pointer'}} value={form.avg_players} onChange={e=>set('avg_players',e.target.value)}><option value="">Select...</option>{PLAYER_COUNT_OPTIONS.map(o=><option key={o} value={o}>{o}</option>)}</select></Box>
            <Box><label style={lbl}>Describe Your Game *</label><Box as="textarea" rows={3} style={{...inp,resize:'none' as any}} placeholder="Format, atmosphere, experience level, location type..." value={form.game_description} onChange={(e:any)=>set('game_description',e.target.value)} /></Box>
            <Box><label style={lbl}>Why do you want PCTDS verification? *</label><Box as="textarea" rows={4} style={{...inp,resize:'none' as any}} placeholder="Tell us why you want to be sanctioned..." value={form.why_pctds} onChange={(e:any)=>set('why_pctds',e.target.value)} /></Box>
            <Box><label style={lbl}>References (optional)</label><Box as="textarea" rows={2} style={{...inp,resize:'none' as any}} placeholder="PokerCity usernames or emails of players who can vouch for your game" value={form.references} onChange={(e:any)=>set('references',e.target.value)} /></Box>

            <Box bg="#0A0A0A" borderRadius={6} p={4}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim, letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:12 }}>Agreements</Text>
              <Flex direction="column" gap={3}>
                {[{field:'agrees_to_rules',text:'I agree to follow PCTDS rules and standards for sanctioned events, including TDA-approved rules and accurate event records.'},{field:'agrees_to_verification',text:'I understand PCTDS may verify my details, contact references, and revoke sanctioning if standards are not maintained.'}].map(item => (
                  <Flex key={item.field} align="flex-start" gap={3} cursor="pointer" onClick={() => set(item.field, !(form as any)[item.field])}>
                    <Box w="20px" h="20px" borderRadius={4} flexShrink={0} mt="2px"
                      bg={(form as any)[item.field] ? C.teal : 'transparent'}
                      border={`2px solid ${(form as any)[item.field] ? C.teal : C.border}`}
                      display="flex" alignItems="center" justifyContent="center">
                      {(form as any)[item.field] && <Text style={{ fontSize:12, color:'#000', fontWeight:700 }}>✓</Text>}
                    </Box>
                    <Text style={{ fontFamily:F.ui, fontSize:13, color:C.ink, lineHeight:1.6 }}>{item.text}</Text>
                  </Flex>
                ))}
              </Flex>
            </Box>

            {error && <Box bg="#2D0D0D" border={`1px solid ${C.red}`} borderRadius={6} p={3}><Text style={{ fontFamily:F.ui, fontSize:13, color:'#FCA5A5' }}>{error}</Text></Box>}

            <Box as="button" px={6} py={4} bg={submitting?C.card:'#1A0F00'} border={`1px solid ${submitting?C.border:C.gold}`} borderRadius={8} fontSize="15px" fontWeight="700" color={submitting?C.dim:C.gold} fontFamily={F.ui} cursor={submitting?'wait':'pointer'} opacity={submitting?0.7:1} onClick={handleSubmit} disabled={submitting}>
              {submitting?'Submitting...':'🏆 Submit PCTDS Application →'}
            </Box>
            <Text style={{ fontFamily:F.ui, fontSize:11, color:C.dim, textAlign:'center' }}>Applications reviewed manually within 3-5 business days.</Text>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default PCTDSApplicationPage;
